import { FavoritesList } from "@/components/layout/components/FavoritesList";
import { RecentsList } from "@/components/layout/components/RecentsList";
import {
  MdsDropdownContentList,
  MdsDropdownItem,
  MdsDropdownItemKind,
} from "@/design-system/components/dropdown";
import { MdsDropdownContent } from "@/design-system/components/dropdown/MdsDropdownContent";
import { MdsIconKind } from "@/design-system/components/icon";
import { mdsColors } from "@/design-system/foundations";
import { css } from "@/domains/emotion";
import { useAppStore } from "@/store";
import { observer } from "mobx-react-lite";
import { useMemo } from "react";
import styled from "@emotion/styled";
import { useMatch } from "react-router";
import { appRoutes } from "@/app/router";
import { Appear } from "@/components/appear";
import { SidebarCreateButtons } from "@/components/layout/components/sidebar/SidebarCreateButtons";

export const SidebarContents = observer(({ onClick }: { onClick?: () => void }) => {
  const { store } = useAppStore();

  const isNotesPath = useMatch(appRoutes.notesList({ starPattern: true }));
  const isCollectionsPath = useMatch(appRoutes.collections({ starPattern: true }));
  const isIntegrationsPath = useMatch(appRoutes.integrations({ starPattern: true }));
  const isExplorePath = useMatch(appRoutes.explore({ starPattern: false }));
  const isFavoriteItemsReady = store.favoriteItems.isReady;
  const isDebugModeEnabled = store.debug.debugModeEnabled;

  const libraryShortcuts: MdsDropdownContentList = useMemo(() => {
    const items: MdsDropdownItem[] = [
      {
        id: "library-heading",
        kind: MdsDropdownItemKind.Detail,
        text: "Library",
      },
      {
        id: "notes",
        kind: MdsDropdownItemKind.Button,
        label: "Notes",
        iconKind: MdsIconKind.Notebook,
        className: isNotesPath ? activeContentListItemStyles : undefined,
        onClick: () => {
          onClick?.();
          store.navigation.goToNotes();
        },
      },
      {
        id: "collections",
        kind: MdsDropdownItemKind.Button,
        label: "Collections",
        iconKind: MdsIconKind.Collection,
        className: isCollectionsPath ? activeContentListItemStyles : undefined,
        onClick: () => {
          onClick?.();
          store.navigation.goToCollections();
        },
      },
      {
        id: "integrations",
        kind: MdsDropdownItemKind.Button,
        label: "Integrations",
        iconKind: MdsIconKind.BoltOutline,
        className: isIntegrationsPath ? activeContentListItemStyles : undefined,
        onClick: () => {
          onClick?.();
          store.navigation.goToIntegrations();
        },
      },
    ];

    if (isDebugModeEnabled) {
      items.push({
        id: "debug-tools",
        kind: MdsDropdownItemKind.Detail,
        text: "Debug Tools",
      });

      items.push({
        id: "explore",
        kind: MdsDropdownItemKind.Button,
        label: "Explore",
        iconKind: MdsIconKind.Compass,
        className: isExplorePath ? activeContentListItemStyles : undefined,
        onClick: () => {
          onClick?.();
          store.navigation.goToExplore();
        },
      });

      items.push({
        id: "sessions",
        kind: MdsDropdownItemKind.Button,
        label: "Sessions",
        iconKind: MdsIconKind.Robot,
        onClick: () => {
          onClick?.();
          store.navigation.goToSessions();
        },
      });
    }

    return {
      items,
    };
  }, [
    isNotesPath,
    isCollectionsPath,
    store.navigation,
    isExplorePath,
    isIntegrationsPath,
    isDebugModeEnabled,
    onClick,
  ]);

  return (
    <StyledAppear visible={isFavoriteItemsReady}>
      <ScrollablePortion>
        <SidebarCreateButtons isCollapsed={false} onClick={onClick} />
        <MdsDropdownContent contentList={libraryShortcuts} className={transparentGroupStyles} />
        <FavoritesList className={transparentGroupStyles} onClick={onClick} animate={false} />
        <RecentsList
          className={transparentGroupStyles}
          header="Recents"
          recentsList={store.recentItems.sortedRecentItems}
          selectedItemClassName={activeContentListItemStyles}
          limit={5}
          onClick={onClick}
          animateItems
        />
      </ScrollablePortion>
    </StyledAppear>
  );
});

const ScrollablePortion = styled.div({
  overflowY: "auto",
  minHeight: 0,
  height: "100%",
  scrollbarWidth: "none",
  "&::-webkit-scrollbar": {
    display: "none",
  },
});
ScrollablePortion.displayName = "ScrollablePortion";

const transparentGroupStyles = css({
  backgroundColor: `transparent`,
  boxShadow: `none`,
  width: `100%`,
  border: 0,
  height: "auto",
});

const activeContentListItemStyles = css({
  backgroundColor: mdsColors().lilac.x50,
});

const StyledAppear = styled(Appear)({
  display: "flex",
  flexDirection: "column",
  flex: 1,
  overflow: "hidden",
});
